import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { title: "", destination: "", start_date: "", end_date: "", price: "", description: "", image_url: "", cities: [], round_trip: false };

export default function Packages() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/packages");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(empty); setEditingId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({
      title: p.title, destination: p.destination, start_date: p.start_date,
      end_date: p.end_date, price: p.price, description: p.description, image_url: p.image_url || "",
      cities: p.cities || [], round_trip: p.round_trip || false,
    });
    setEditingId(p.id);
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0 };
      if (editingId) {
        await api.put(`/packages/${editingId}`, payload);
        toast.success("Package updated");
      } else {
        await api.post("/packages", payload);
        toast.success("Package created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this package?")) return;
    try {
      await api.delete(`/packages/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="packages-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">Catalog</div>
          <h1 className="font-display text-4xl mt-2 text-[#0F172A]">Travel Packages</h1>
          <p className="text-[#64748B] mt-2 text-sm">Curate the journeys you offer your customers.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="bg-[#1E3A8A] hover:bg-[#172A6B] text-white" data-testid="add-package-btn">
              <Plus size={16} className="mr-2" /> Add package
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">{editingId ? "Edit package" : "Add package"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="package-title-input" />
              </div>
              <div className="space-y-2">
                <Label>Destination</Label>
                <Input value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} data-testid="package-destination-input" />
              </div>
              <div className="space-y-2">
                <Label>Price (INR)</Label>
                <Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="package-price-input" />
              </div>
              <div className="space-y-2">
                <Label>Start date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="package-start-input" />
              </div>
              <div className="space-y-2">
                <Label>End date</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="package-end-input" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Image URL (optional)</Label>
                <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="package-image-input" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Description</Label>
                <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="package-description-input" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Route (cities)</Label>
                <div className="space-y-2">
                  {form.cities.map((c, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <Input value={c} onChange={(e) => {
                        const next = [...form.cities]; next[idx] = e.target.value; setForm({ ...form, cities: next });
                      }} placeholder={`City ${idx + 1}`} />
                      <Button variant="outline" onClick={() => { const next = form.cities.filter((_, i) => i !== idx); setForm({ ...form, cities: next }); }}>Remove</Button>
                    </div>
                  ))}
                  <div className="flex space-x-2">
                    <Button size="sm" onClick={() => setForm({ ...form, cities: [...(form.cities || []), ""] })}>Add city</Button>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" checked={form.round_trip} onChange={(e) => setForm({ ...form, round_trip: e.target.checked })} />
                      <span className="text-sm">Round trip (end equals start)</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="package-cancel-btn">Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-[#1E3A8A] hover:bg-[#172A6B] text-white" data-testid="package-save-btn">
                {saving ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-2xl card-soft overflow-hidden" data-testid="packages-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Destination</TableHead>
              <TableHead>Dates</TableHead>
              <TableHead>Price</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-[#64748B]">No packages yet.</TableCell>
              </TableRow>
            ) : (
              items.map((p) => (
                <TableRow key={p.id} data-testid={`package-row-${p.id}`}>
                  <TableCell className="font-medium">{p.title}</TableCell>
                  <TableCell>{p.destination}</TableCell>
                  <TableCell>{p.start_date} → {p.end_date}</TableCell>
                  <TableCell>₹{Number(p.price).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-package-${p.id}`}>
                      <Pencil size={14} />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => del(p.id)} data-testid={`delete-package-${p.id}`}>
                      <Trash2 size={14} className="text-[#DC2626]" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
